import { Component, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ModalController, PopoverController } from "@ionic/angular";
import moment from "moment";
import { IMqttMessage } from "ngx-mqtt";
import { Subscription } from "rxjs";
import { TimeSelectorComponent } from "src/components/general components/time-selector/time-selector.component";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import {
  ChartSerieKind,
  ChartSeriePointStyle,
  ChartTimeUM,
} from "src/components/shared-components/enum/enumChart";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { GenericBasicGUI } from "src/model/class/component classes/generic-basic-gui";
import { ChartSerieValue } from "src/model/class/general classes/chart-serie-value";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { AlertService } from "src/service/alert.service";
import { ChartService } from "src/service/chart.service";
import { LoaderService } from "src/service/loader.service";
import { LoggerService } from "src/service/logger.service";
import { TranslationService } from "src/service/translation.service";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Component({
    selector: "app-chart",
    templateUrl: "./chart.component.html",
    styleUrls: ["./chart.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
// tslint:disable: no-string-literal
export class ChartComponent
  extends GenericBasicGUI
  implements OnInit, iTranslation, iMqttHandling {
  private _chartService: ChartService;
  private _translationService: TranslationService;
  private _loggerService: LoggerService;
  private _popoverCtrl: PopoverController;
  private _alertService: AlertService;
  private _loaderService: LoaderService;
  private _chartInstance;
  private _serieLoaded: Map<number, any> = new Map<number, any>();
  private _startDate: Date;
  private _endDate: Date;
  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;
  private _yMaxValueChart: number = -1;
  private _yMinValueChart: number = -1;
  private ySplitNumber: number = 4;
  private typeXAxis = null;
  private legendColors = [];
  private _typeZoom: string = "";

  public chartSerieKind = ChartSerieKind;
  public chartKind: ChartSerieKind = ChartSerieKind.LINE;
  public chartGranularity = "12345";
  public lblSelect: string = "";
  public lblCancel: string = "";
  public lblWeek: string = "";
  public lblFrom: string = "";
  public lblTo: string = "";
  public xMinValue: Date;
  public xMaxValue: Date;
  public granularity: ChartTimeUM = ChartTimeUM.HOUR;
  public chartTitle: string;
  public showTitle: boolean = true;
  public isPeriodEnergy: boolean = true;

  // arrayOfSeries contains the series that we have to pass to the chart options, and contains the series and their values
  private arrayOfSeries: any[] = [];
  private seriePIE: any[] = [];

  options = {};

  datas: any = null;

  public titleStyle = {
    "padding-top": "10px",
    "padding-bottom": "25px",
    "font-family": "Arial",
    color: "#000000",
    "font-size": "15px",
    "font-style": "normal", // italic
    "font-weight": "normal", // bol
    "font-decorator": "none", // underline
    "text-align": "center",
  };

  public styles = {
    "font-family": "Arial",
    color: "#000000",
    "font-size": "15px",
    "font-style": "normal", // italic
    "font-weight": "normal", // bol
    "font-decorator": "none", // underline
    left: "0px",
    top: "0px",
    width: "10px",
    height: "10px",
    position: "absolute",
    "align-items": "center",
    "justify-content": "center",
    display: "flex",
  };

  public chartStyle = {
    position: "relative",
    width: "100%",
    height: "100%",
    left: "0px",
    "backface-visibility": "hidden",
    transform: "translateZ(0)",
    "-webkit-font-smoothing": "subpixel-antialiased",
  };

  public stylesChild = {};

  constructor (injector: Injector, private _modalCtrl: ModalController) {
    super(injector);
    this._chartService = injector.get(ChartService);
    this._popoverCtrl = injector.get(PopoverController);
    this._alertService = injector.get(AlertService);
    this._translationService = injector.get(TranslationService);
    this._loggerService = injector.get(LoggerService);
    this._loaderService = injector.get(LoaderService);
    this._logContext = "Chart.component";
  }

  reloadChartSize() { }

  /**
   * Load all the properties of the charts and its data
   */
  async ngOnInit() {
    this._chartService.Init();
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();
    this.loadTranslation();
    this.subscribeTranslationEvent();

    [this.styles, this.stylesChild] = super.HandleProperties(
      this.styles,
      this.stylesChild
    );

    this.chartStyle["height"] = this.styles["height"];
    this.chartStyle["width"] = this.styles["width"];
    this._typeZoom = "slider";
    if (!this.ShowingInDesktopVersion) {
      this.styles["width"] = "100%";
      this.styles["height"] = "600px";

      this.chartStyle["width"] = this.styles["width"];
      this.chartStyle["height"] = "350px";
    }
    if (this.platform.is("android") || this.platform.is("ios")) {
      this._typeZoom = "inside";
    }

    moment.locale(this._translationService.currentLanguage);

    // default x axis type
    this.typeXAxis = "time";

    this.granularity = parseInt(
      this.ComponentAPI.properties["Granularity"],
      10
    );

    this.loadChartPeriod();

    const listGranularity = await this._chartService.getChartGranularity(
      this.ComponentAPI.properties["Chart"]
    );
    this.chartGranularity = listGranularity["ids"].join("");
    let numElementforPeriod = this.ComponentAPI.chartSeries.length;

    for (const chartSerieAPI of this.ComponentAPI.chartSeries) {
      // default value to be replaced by real chart serie settings
      if (chartSerieAPI.address.indexOf("PeriodEnergy/") == -1) {
        this.isPeriodEnergy = false;
      }
      let serie: any;
      serie = {
        data: [],
        type: "line",
        lineStyle: {
          color: chartSerieAPI["penColorRGB"],
          //width: chartSerieAPI["penWidth"],
        },
        itemStyle: {
          color: chartSerieAPI["penColorRGB"],
          width: chartSerieAPI["penWidth"],
        },
        symbolSize: chartSerieAPI.pointSize,
        smooth: chartSerieAPI.smoothed === "true",
        symbol: "circle",
        areaStyle: null,
        label: {
          show: false,
          fontSize: 15,
          color: "black",
          position: "top",
        },
      };

      if (parseInt(chartSerieAPI.kind, 10) == ChartSerieKind.AREA) {
        serie = {
          data: [],
          type: "line",
          symbol: "circle",
          lineStyle: {
            color: chartSerieAPI["penColorRGB"],
            //width: chartSerieAPI["penWidth"],
          },
          itemStyle: {
            color: chartSerieAPI["penColorRGB"],
          },
          symbolSize: chartSerieAPI.pointSize,
          smooth: chartSerieAPI.smoothed.toString() == "true",
          areaStyle: null,
          label: {
            show: false,
            fontSize: 15,
            color: "black",
            position: "top",
          },
        };
      }
      if (parseInt(chartSerieAPI.kind, 10) == ChartSerieKind.PIE) {
        this.arrayOfSeries = [
          {
            data: [],
            name: "Pie",
            type: "pie",
            radius: ["15%", "55%"],
            center: ["50%", "50%"],
            animationType: "scale",
            itemStyle: {
              borderRadius: 5,
              borderColor: "#fff",
              borderWidth: 5,
            },
            animationEasing: "elasticOut",
            animationDelay: function (idx) {
              return Math.random() * 200;
            },
          },
        ];
      }
      this.legendColors.push(chartSerieAPI["penColorRGB"]);

      switch (parseInt(chartSerieAPI.kind, 10)) {
        case ChartSerieKind.LINE:
          serie.type = "line";
          this.chartKind = ChartSerieKind.LINE;
          break;
        case ChartSerieKind.AREA:
          serie.type = "line";
          serie.areaStyle = {};
          //this.options["xAxis"]["min"] = this.xMinValue;
          //this.options["xAxis"]["max"] = this.xMaxValue;
          this.chartKind = ChartSerieKind.AREA;
          break;
        case ChartSerieKind.BAR:
          serie.type = "bar";
          this.typeXAxis = "category";

          this.chartKind = ChartSerieKind.BAR;
          if (
            !this.listViewMode ||
            (this.listViewMode &&
              !this.platform.is("android") &&
              !this.platform.is("ios"))
          ) {
            serie.label.show = true;
            serie.label["formatter"] = this.loadTopBarValueLabel(serie);
          } else {
            serie.label.show = false;
          }
          break;
        case ChartSerieKind.PIE:
          serie = {
            //symbolSize: chartSerieAPI.pointSize,
            //mooth: chartSerieAPI.smoothed === 'true',
            //symbol: 'circle',
            data: [],
            type: "pie",
            radius: "55%",
            center: ["40%", "50%"],
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: "rgba(0, 0, 0, 0.5)",
              },
            },
          };
          this.chartKind = ChartSerieKind.PIE;
          break;
        default:
          break;
      }

      if (this.chartKind != ChartSerieKind.PIE) {
        serie.symbol = this.getPieChartSymbol(chartSerieAPI.pointStyle);

        if (chartSerieAPI.pointVisible.toString() != "true") {
          serie.symbol = "none";
        }
      }

      this._serieLoaded.set(chartSerieAPI.id, serie);

      let lenghtSerie = await this.loadSerieData(
        chartSerieAPI,
        this.xMinValue,
        this.xMaxValue
      );

      if (this.ComponentAPI.properties["RealTimeUpdating"] == "TRUE") {
        await this.subscribeToChartSerieAPIMQTTAddress(chartSerieAPI, serie);
      }

      if (this.chartKind !== ChartSerieKind.PIE) {
        this.arrayOfSeries.push(serie);
      }
    }

    // });
    let yMinValue = "dataMin"; //
    let yMaxValue = "dataMax";

    if (
      this.ComponentAPI.properties["YLeftAutomatic"].toUpperCase() == "FALSE"
    ) {
      yMinValue = parseFloat(
        this.ComponentAPI.properties["YLeftMinValue"]
      ).toString();
      yMaxValue = parseFloat(
        this.ComponentAPI.properties["YLeftMaxValue"]
      ).toString();
      this.ySplitNumber =
        (parseFloat(yMaxValue) - parseFloat(yMinValue)) /
        parseFloat(this.ComponentAPI.properties["YLeftStep"].replace(",", "."));
    } else {
      if (this.chartKind == ChartSerieKind.BAR) {
        yMinValue = "0";
        yMaxValue = this._yMaxValueChart.toString();
      }
    }

    this.titleStyle["color"] = this.ComponentAPI.properties["FontColor"];
    this.titleStyle["font-family"] = this.ComponentAPI.properties["FontFamily"];
    (this.titleStyle["font-style"] =
      this.ComponentAPI.properties["Italic"].toUpperCase() === "TRUE"
        ? "italic"
        : "normal"),
      (this.titleStyle["font-weight"] =
        this.ComponentAPI.properties["Bold"].toUpperCase() === "TRUE"
          ? "bold"
          : "normal");

    // GF: options get from https://echarts.apache.org/en/option.html

    if (
      this.chartKind == ChartSerieKind.BAR ||
      this.chartKind == ChartSerieKind.LINE ||
      this.chartKind == ChartSerieKind.AREA
    ) {
      yMaxValue = this._yMaxValueChart.toString();

      this.loadOptionChart(
        yMaxValue,
        this._yMinValueChart,
        numElementforPeriod * this.arrayOfSeries.length
      );
    } else if (this.chartKind == ChartSerieKind.PIE) {
      this.loadPieChartOptions();
    }

    this.loadChartTitle();
  }

  ngOnDestroy() {
    this.unsubscribeToMQTTAddresses();

    this.onDestroyChildren();
  }

  private loadOptionChart(yMaxValue, yMinValue, numElementforPeriod) {
    if (numElementforPeriod == 0) {
      yMaxValue = 0;
    }

    let bottom = 30;
    if (this.ComponentAPI.properties["ShowLegend"].toUpperCase() == "FALSE")
      bottom -= 10;

    if (this.chartKind == ChartSerieKind.BAR) bottom -= 0;
    if (this.chartKind == ChartSerieKind.BAR) {
      yMinValue = "dataMin";

      this.options = {
        grid: [
          {
            backgroundcolor: this.ComponentAPI.properties["BackColor"],
            top: "10%",
            bottom: bottom + "%",
            left: "15%",
          },
        ],
        legend: {
          show:
            this.ComponentAPI.properties["ShowLegend"]
              .toString()
              .toUpperCase() == "TRUE",
          type: "scroll",
          padding: 0,

          textStyle: {
            color: this.ComponentAPI.properties["LegendFontColor"],
            fontFamily: this.ComponentAPI.properties["LegendFontFamily"],
            fontSize: this.ComponentAPI.properties["LegendFontSize"],
            fontStyle:
              this.ComponentAPI.properties["LegendItalic"].toUpperCase() ===
                "TRUE"
                ? "italic"
                : "normal",
            fontWeight:
              this.ComponentAPI.properties["LegendBold"].toUpperCase() ===
                "TRUE"
                ? "bold"
                : "normal",
          },
        },
        tooltip: {
          trigger: "axis",
          confine: true,
          axisPointer: {
            type: "shadow",
            animation: true,
          },
          formatter: (params) => {
            //let textTooltip = params[0]?.axisValueLabel;
            let textTooltip = "";
            params.forEach((item) => {
              textTooltip +=
                this.formatXAxisTexForBarChart(item.data[3], true) +
                "<br>" +
                item.seriesName +
                ": " +
                item.data[1].toString().replace(".", ",");
              if (item.data[2]) {
                textTooltip += " " + item.data[2];
              }
            });
            return textTooltip;
          },
        },
        xAxis: {
          type: this.typeXAxis,
          name: this.ComponentAPI.properties["XBottomTitle"],
          splitLine: {
            show:
              this.ComponentAPI.properties[
                "XBottomGridVisible"
              ].toUpperCase() == "TRUE",
          },
          nameTextStyle: {
            color: this.ComponentAPI.properties["XBottomFontColor"],
            fontFamily: this.ComponentAPI.properties["XBottomFontFamily"],
            fontSize: this.ComponentAPI.properties["XBottomFontSize"],
            fontStyle:
              this.ComponentAPI.properties["XBottomItalic"].toUpperCase() ===
                "TRUE"
                ? "italic"
                : "normal",
            fontWeight:
              this.ComponentAPI.properties["XBottomBold"].toUpperCase() ===
                "TRUE"
                ? "bold"
                : "normal",
          },
          axisLabel: {
            interval: 0,
            rotate: 30,
            color: this.ComponentAPI.properties["XBottomFontColor"],
            fontFamily: this.ComponentAPI.properties["XBottomFontFamily"],
            fontSize: this.ComponentAPI.properties["XBottomFontSize"],
            fontStyle:
              this.ComponentAPI.properties["XBottomItalic"].toUpperCase() ===
                "TRUE"
                ? "italic"
                : "normal",
            fontWeight:
              this.ComponentAPI.properties["XBottomBold"].toUpperCase() ===
                "TRUE"
                ? "bold"
                : "normal",
            formatter: this.getXAsisTextFormatting.bind(this),
          },
        },
        yAxis: {
          type: "value",
          max: (yMaxValue * 1.1).toFixed(0).toString(),
          show: true,
          splitNumber: this.ySplitNumber,
          name: this.ComponentAPI.properties["YLeftTitle"],
          nameLocation: "middle",
          splitLine: {
            show:
              this.ComponentAPI.properties["YLeftGridVisible"].toUpperCase() ==
              "TRUE",
          },
          nameTextStyle: {
            color: this.ComponentAPI.properties["YLeftFontColor"],
            fontFamily: this.ComponentAPI.properties["YLeftFontFamily"],
            fontSize: this.ComponentAPI.properties["YLeftFontSize"],
            fontStyle:
              this.ComponentAPI.properties["YLeftItalic"].toUpperCase() ===
                "TRUE"
                ? "italic"
                : "normal",
            fontWeight:
              this.ComponentAPI.properties["YLeftBold"].toUpperCase() === "TRUE"
                ? "bold"
                : "normal",
          },
          axisLabel: {
            color: this.ComponentAPI.properties["YLeftFontColor"],
            fontFamily: this.ComponentAPI.properties["YLeftFontFamily"],
            fontSize: this.ComponentAPI.properties["YLeftFontSize"],
            fontStyle:
              this.ComponentAPI.properties["YLeftItalic"].toUpperCase() ===
                "TRUE"
                ? "italic"
                : "normal",
            fontWeight:
              this.ComponentAPI.properties["YLeftBold"].toUpperCase() === "TRUE"
                ? "bold"
                : "normal",
            formatter: function (value, index) {
              return value.toString().replace(".", ",");
            },
          },
        },
        series: this.arrayOfSeries,
        color: this.legendColors,
      };
    }

    if (
      this.chartKind == ChartSerieKind.AREA ||
      this.chartKind == ChartSerieKind.LINE
    ) {
      this.options = {
        grid: [
          {
            backgroundcolor: this.ComponentAPI.properties["BackColor"],
            top: "5%",
            bottom: bottom + "%",
            left: "15%",
          },
        ],
        legend: {
          show:
            this.ComponentAPI.properties["ShowLegend"]
              .toString()
              .toUpperCase() == "TRUE",
          type: "scroll",
          textStyle: {
            color: this.ComponentAPI.properties["LegendFontColor"],
            fontFamily: this.ComponentAPI.properties["LegendFontFamily"],
            fontSize: this.ComponentAPI.properties["LegendFontSize"],
            fontStyle:
              this.ComponentAPI.properties["LegendItalic"].toUpperCase() ===
                "TRUE"
                ? "italic"
                : "normal",
            fontWeight:
              this.ComponentAPI.properties["LegendBold"].toUpperCase() ===
                "TRUE"
                ? "bold"
                : "normal",
          },
        },
        tooltip: {
          trigger: "axis",
          confine: true,
          axisPointer: {
            type: "cross",
            animation: true,
          },
          formatter: (params) => {
            let textTooltip = this.formatXAxisTexForLineChart(
              params[0]?.axisValue
            );
            params.forEach((item) => {
              textTooltip +=
                "<br>" +
                item.seriesName +
                ": " +
                item.data[1].toString().replace(".", ",");
              if (item.data[2]) {
                textTooltip += " " + item.data[2];
              }
            });
            return textTooltip;
          },
        },
        xAxis: {
          type: this.typeXAxis,
          boundaryGap: false,
          name: this.ComponentAPI.properties["XBottomTitle"],
          splitLine: {
            show:
              this.ComponentAPI.properties[
                "XBottomGridVisible"
              ].toUpperCase() == "TRUE",
          },
          nameTextStyle: {
            color: this.ComponentAPI.properties["XBottomFontColor"],
            fontFamily: this.ComponentAPI.properties["XBottomFontFamily"],
            fontSize: this.ComponentAPI.properties["XBottomFontSize"],
            fontStyle:
              this.ComponentAPI.properties["XBottomItalic"].toUpperCase() ===
                "TRUE"
                ? "italic"
                : "normal",
            fontWeight:
              this.ComponentAPI.properties["XBottomBold"].toUpperCase() ===
                "TRUE"
                ? "bold"
                : "normal",
          },
          axisLabel: {
            interval: 0,
            rotate: 30,
            color: this.ComponentAPI.properties["XBottomFontColor"],
            fontFamily: this.ComponentAPI.properties["XBottomFontFamily"],
            fontSize: this.ComponentAPI.properties["XBottomFontSize"],
            fontStyle:
              this.ComponentAPI.properties["XBottomItalic"].toUpperCase() ===
                "TRUE"
                ? "italic"
                : "normal",
            fontWeight:
              this.ComponentAPI.properties["XBottomBold"].toUpperCase() ===
                "TRUE"
                ? "bold"
                : "normal",
            formatter: this.getXAsisTextFormatting.bind(this),
          },
        },
        yAxis: {
          type: "value",
          show: true,
          min: yMinValue,
          splitNumber: this.ySplitNumber,
          name: this.ComponentAPI.properties["YLeftTitle"],
          nameLocation: "middle",
          splitLine: {
            show:
              this.ComponentAPI.properties["YLeftGridVisible"].toUpperCase() ==
              "TRUE",
          },
          nameTextStyle: {
            color: this.ComponentAPI.properties["YLeftFontColor"],
            fontFamily: this.ComponentAPI.properties["YLeftFontFamily"],
            fontSize: this.ComponentAPI.properties["YLeftFontSize"],
            fontStyle:
              this.ComponentAPI.properties["YLeftItalic"].toUpperCase() ===
                "TRUE"
                ? "italic"
                : "normal",
            fontWeight:
              this.ComponentAPI.properties["YLeftBold"].toUpperCase() === "TRUE"
                ? "bold"
                : "normal",
          },
          axisLabel: {
            color: this.ComponentAPI.properties["YLeftFontColor"],
            fontFamily: this.ComponentAPI.properties["YLeftFontFamily"],
            fontSize: this.ComponentAPI.properties["YLeftFontSize"],
            fontStyle:
              this.ComponentAPI.properties["YLeftItalic"].toUpperCase() ===
                "TRUE"
                ? "italic"
                : "normal",
            fontWeight:
              this.ComponentAPI.properties["YLeftBold"].toUpperCase() === "TRUE"
                ? "bold"
                : "normal",
            formatter: function (value, index) {
              return value.toString().replace(".", ",");
            },
          },
        },
        series: this.arrayOfSeries,
        color: this.legendColors,
      };
    }

    if (this.chartKind == ChartSerieKind.BAR) {
      this.options["dataZoom"] = [
        { zoomOnMouseWheel: true, type: this._typeZoom },
      ];
      if (numElementforPeriod > 6) {
        let maxSpanCalculated = (6 * 100) / numElementforPeriod;
        this.options["dataZoom"] = [
          {
            zoomOnMouseWheel: true,
            type: this._typeZoom,
            maxSpan: maxSpanCalculated,
          },
        ];
      }
    }
    if (
      this.chartKind == ChartSerieKind.LINE ||
      this.chartKind == ChartSerieKind.AREA
    ) {
      this.options["dataZoom"] = [
        { zoomOnMouseWheel: true, type: this._typeZoom },
      ];
      if (numElementforPeriod > 150) {
        let maxSpanCalculated = (150 * 100) / numElementforPeriod;

        this.options["dataZoom"] = [
          {
            zoomOnMouseWheel: true,
            type: this._typeZoom,
            maxSpan: maxSpanCalculated,
          },
        ];
      }
    }

    this._chartInstance.setOption(this.options, true);
  }

  /**
   *
   * @param serie
   * @param date
   * @param value
   * @param realTimeUpdate
   */
  public addValueToChart(
    serie: any,
    date: Date,
    value: number,
    realTimeUpdate: boolean,
    um?: String
  ): void {
    switch (serie.type) {
      // line charts are used to show changes of values in time, so the X axis is a time axis and the x coordinate is a date
      case "line":
        let dateStringLine = this.formatXAxisTexForBarChart(
          date,
          this.ShowingInDesktopVersion
        );

        serie.stack = dateStringLine + "_" + serie.name;
        serie.data.push([moment(date).format(), value, um, dateStringLine]); //

        break;
      case "bar":
        let dateString = this.formatXAxisTexForBarChart(
          date,
          this.ShowingInDesktopVersion
        );

        serie.data.push([dateString, value, um, date]); //dateString

        serie.stack = dateString + "_" + serie.name;
        /*serie.data.push({
          value: [dateString, 200],
          itemStyle: {
            color: "#a90000",
          },
        });*/
        break;
      case "pie":
        /*this.arrayOfSeries[0]["data"] = this.arrayOfSeries[0]["data"].filter(
          (chartValue, index) => chartValue.name !== serie.name
        );*/
        //serie.data = serie.data.filter((chartValue, index) => chartValue.name !== serie.name );

        this.seriePIE.push({
          value: value,
          name: serie.name,
          um: um,
        });

        /*this.arrayOfSeries[0]["data"].push({
          value: value,
          name: serie.name,
        });
        this.arrayOfSeries[0]["data"].sort(function (a, b) {
          return a.name < b.name;
        });*/

        break;
      default:
        break;
    }
  }

  /**
   * Load chart title and check
   */
  public loadChartTitle(): void {
    this.showTitle =
      this.ComponentAPI.properties["ShowText"].toUpperCase() == "TRUE";

    this.chartTitle = this.keywordTextFormatterAdapter.replaceText(
      null,
      null,
      null,
      this.currentChart
    );
  }

  /**
   * Generate the label to put on the top of the bar in order to show the value of the bar
   * @param serie
   * @returns
   */
  public loadTopBarValueLabel = (serie) => {
    let um: string = "";
    return (param) => {
      let sum = 0;
      serie.data.forEach((item) => {
        if (item[0] == param.name) sum += item[1];
        if (item[2]) {
          um = item[2];
        }
      });
      return (
        sum.toFixed(this._settings.DefaultDecimalDigits).replace(".", ",") +
        " " +
        um
      );
    };
  };

  /**
   * GF: Load the data of a specific serie
   * @param chartSerieAPI: the serie (object is taken from the object returned by the API)
   * @param startDate start date since when I have to load the data
   * @param endDate end date until I have to load the data
   */
  public async loadSerieData(
    chartSerieAPI,
    startDate,
    endDate
  ): Promise<number> {
    let isPeriodEnergy: boolean = false;
    let isDataProcess: boolean = false;
    let serie = this._serieLoaded.get(chartSerieAPI.id);
    serie.name = chartSerieAPI.legendText;
    let loadedChartSerieValues: Array<ChartSerieValue> = null;

    if (chartSerieAPI.address.toString().substring(0, 12) == "PeriodEnergy") {
      this.isPeriodEnergy = true;
    }
    try {
      if (
        chartSerieAPI.instanceGUID !== "" &&
        chartSerieAPI.address !== "" &&
        !this.isPeriodEnergy
      ) {
        loadedChartSerieValues =
          await this._chartService.getChartSeriesValuesByAddress(
            chartSerieAPI.instanceGUID,
            chartSerieAPI.address,
            moment(startDate),
            moment(endDate),
            this.chartKind
          );

        if (this.chartKind == ChartSerieKind.PIE) {
          if (loadedChartSerieValues.length > 0) {
            loadedChartSerieValues[0].value = Number(
              loadedChartSerieValues[0].value.toFixed(
                this._settings.DefaultDecimalDigits
              )
            );
          }
        }
      } else if (
        parseInt(chartSerieAPI.idDataProcess, 10) > 0 ||
        this.isPeriodEnergy
      ) {
        isDataProcess = true;
        let dataProcess = chartSerieAPI.idDataProcess;
        if (this.isPeriodEnergy) {
          let arSplit = chartSerieAPI.address.toString().split("/");
          dataProcess = arSplit[1];
        }

        loadedChartSerieValues =
          await this._chartService.getChartSeriesValuesByIDDataProcess(
            dataProcess,
            moment(startDate),
            moment(endDate),
            this.chartKind
          );
      }
    } catch {
      this._alertService.presentAlertSomethingWrong();
    }
    // if I have values in the chart serie I load them
    if (loadedChartSerieValues && loadedChartSerieValues != null) {
      if (loadedChartSerieValues.length > 0) {
        let date;
        loadedChartSerieValues.forEach((loadedValue) => {
          date = ChartComponent.getMomentFromDateTimeString(
            loadedValue.day + " " + loadedValue.time
          );
          if (isDataProcess && this.granularity == ChartTimeUM.HOUR) {
            date = ChartComponent.getMomentFromDateTimeString(
              loadedValue.timeStampEndProcess
            );
          }
          this.addValueToChart(
            serie,
            date.toDate(),
            loadedValue.value,
            false,
            loadedValue.um
          );

          if (loadedValue.value > this._yMaxValueChart) {
            this._yMaxValueChart = loadedValue.value;
          }
          if (
            this._yMinValueChart == -1 ||
            loadedValue.value < this._yMinValueChart
          ) {
            this._yMinValueChart = loadedValue.value;
          }
        });
        //}
      } else {
        this._yMaxValueChart = 0;
        this._yMinValueChart = 0;
        this.addValueToChart(serie, moment().toDate(), 0, false, "");
      }

      return loadedChartSerieValues.length;
    }
  }

  /**
   * Opens the popup to select the time.
   */
  public async openTimeSelector(value: number) {
    let minDate = value == 1 ? "" : this.xMinValue.toISOString();
    let maxDate = value == 1 ? this.xMaxValue.toISOString() : "";
    if (this.chartKind == this.chartSerieKind.PIE) {
      minDate = "";
      maxDate = this.xMaxValue.toISOString();
    }
    const modalTimeSelector = await this._modalCtrl.create({
      component: TimeSelectorComponent,
      cssClass: "modal-date-selector",
      componentProps: {
        actualStartDatePicker:
          value == 1
            ? this.xMinValue.toISOString()
            : this.xMaxValue.toISOString(),
        actualStartTimePicker: "",
        isDate: true,
        isTime: false,
        minDate: minDate,
        maxDate: maxDate,
      },
      animated: true,
      showBackdrop: true,
    });

    modalTimeSelector.present();

    const { data } = await modalTimeSelector.onDidDismiss();

    if (modalTimeSelector) {
      modalTimeSelector.dismiss();
    }

    if (data) {
      if (value == 1) {
        this._startDate = new Date(data.date);
      } else if (value == 2) {
        this._endDate = new Date(data.date);
      }
      this.periodChanged();
    }
  }

  /**
   * Public chart init event
   *
   * @param ec
   */
  public onChartInit(ec): void {
    this._chartInstance = ec;
  }

  async periodChanged() {
    this._endDate.setHours(23, 59, 59, 0);
    this._startDate.setHours(0, 0, 0, 0);
    this.xMaxValue = this._endDate;
    this.xMinValue = this._startDate;
    await this._loaderService.presentLoaderWaiting();
    await this.reloadDataAndSetOption();
    await this._loaderService.dismissLoaderWaiting();
  }

  startPeriodChanged(event: any) {
    this._startDate = moment(
      event,
      MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_INVERSE
    ).toDate();
  }

  async endPeriodChanged(event: any) {
    this._endDate = moment(
      event,
      MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_INVERSE
    ).toDate();
  }

  getTimeUMStringAdjectiveStatic(value: ChartTimeUM): string {
    return StaticClass.getTimeUMString(value);
  }

  //#region MQTT-address handling

  subscribeMQTTAddresses(): void { }

  private async subscribeToChartSerieAPIMQTTAddress(
    chartSerieAPI: any,
    serie: any
  ): Promise<void> {
    const mqttAddress =
      chartSerieAPI.instanceGUID + "_" + chartSerieAPI.address;

    const returnedObservable = await this.mqttService.subscribeTopic(
      mqttAddress
    );

    const mqttAddressSubcription = returnedObservable.subscribe(
      async (message: IMqttMessage) => {
        const payloadMessage = JSON.parse(message.payload.toString()).Message;

        this.seriePIE = [];
        setTimeout(async () => {
          this.xMaxValue = moment().toDate();
          await this.reloadDataAndSetOption();
        }, 2000);
      }
    );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  //#endregion

  //#region Translation
  public loadTranslation(): void {
    this.translateServiceTraductionService
      .get([
        "dictionary.undo",
        "dictionary.select",
        "dictionary.week",
        "dictionary.fromDate",
        "dictionary.toDate",
      ])
      .subscribe((translation) => {
        this.lblSelect = translation["dictionary.select"];
        this.lblCancel = translation["dictionary.undo"];
        this.lblWeek = translation["dictionary.week"];
        this.lblFrom = translation["dictionary.fromDate"];
        this.lblTo = translation["dictionary.toDate"];
      });
  }

  public subscribeTranslationEvent(): void {
    this.translateServiceTraductionService.onLangChange.subscribe(() => {
      this.loadTranslation();
    });
  }
  //#endregion

  /**
   * Handle click go back to go back in time chart
   */
  public async onClickGoBack(): Promise<void> {
    await this._loaderService.presentLoaderWaiting();
    let tempXMinValue = moment().toDate();
    let tempXMaxValue = moment().toDate();

    switch (this.granularity) {
      case ChartTimeUM.HOUR:
        tempXMinValue = moment(this.xMinValue)
          .subtract(1, "days")
          .hours(1)
          .minutes(0)
          .seconds(0)
          .toDate();
        tempXMaxValue = moment(tempXMinValue).add(1, "days").toDate();
        break;
      case ChartTimeUM.DAY:
        tempXMinValue = moment(this.xMinValue)
          .subtract(1, "weeks")
          .startOf("isoWeek")
          .hours(1)
          .minutes(0)
          .seconds(0)
          .toDate();
        tempXMaxValue = moment(tempXMinValue).add(1, "weeks").toDate();
        break;
      case ChartTimeUM.WEEK:
        tempXMinValue = moment(this.xMinValue)
          .subtract(1, "months")
          .startOf("month")
          .hours(1)
          .minutes(0)
          .seconds(0)
          .toDate();
        tempXMaxValue = moment(tempXMinValue).add(1, "months").toDate();
        break;
      case ChartTimeUM.MONTH:
        tempXMinValue = moment(this.xMinValue)
          .subtract(1, "years")
          .startOf("years")
          .hours(1)
          .minutes(0)
          .seconds(0)
          .toDate();

        tempXMaxValue = moment(tempXMinValue).add(1, "years").toDate();
        break;
      case ChartTimeUM.YEAR:
        tempXMinValue = moment(this.xMinValue)
          .subtract(5, "years")
          .startOf("years")
          .hours(1)
          .minutes(0)
          .seconds(0)
          .toDate();
        tempXMaxValue = moment(tempXMinValue).add(5, "years").toDate();
        break;
      default:
        const dayDiff = moment(this.xMaxValue)
          .add(1, "seconds")
          .diff(moment(this.xMinValue), "hours");
        tempXMaxValue = this.xMinValue;
        tempXMinValue = moment(this.xMinValue)
          .subtract(dayDiff, "hours")
          .toDate();
        break;
    }

    this.xMinValue = tempXMinValue;
    this.xMaxValue = tempXMaxValue;

    this._endDate = this.xMaxValue;
    this._startDate = this.xMinValue;

    await this._loaderService.dismissLoaderWaiting();
    await this.reloadDataAndSetOption();
  }

  /**
   * Handle click go fortward to move forward chart
   */

  public async onClickGoForward(): Promise<void> {
    await this._loaderService.presentLoaderWaiting();
    this.seriePIE = [];
    let tempXMinValue = moment().toDate();
    let tempXMaxValue = moment().toDate();

    switch (this.granularity) {
      case ChartTimeUM.HOUR:
        tempXMinValue = moment(this.xMinValue)
          .add(1, "days")
          .hours(1)
          .minutes(0)
          .seconds(0)
          .toDate();
        tempXMaxValue = moment(tempXMinValue).add(1, "days").toDate();
        break;
      case ChartTimeUM.DAY:
        tempXMinValue = moment(this.xMinValue)
          .add(1, "weeks")
          .startOf("isoWeek")
          .hours(1)
          .minutes(0)
          .seconds(0)
          .toDate();
        tempXMaxValue = moment(tempXMinValue).add(1, "weeks").toDate();
        break;
      case ChartTimeUM.WEEK:
        tempXMinValue = moment(this.xMinValue)
          .add(1, "months")
          .startOf("month")
          .hours(1)
          .minutes(0)
          .seconds(0)
          .toDate();
        tempXMaxValue = moment(tempXMinValue).add(1, "months").toDate();

        break;
      case ChartTimeUM.MONTH:
        tempXMinValue = moment(this.xMinValue)
          .add(1, "years")
          .startOf("years")
          .hours(1)
          .minutes(0)
          .seconds(0)
          .toDate();
        tempXMaxValue = moment(tempXMinValue).add(1, "years").toDate();
        break;
      case ChartTimeUM.YEAR:
        tempXMinValue = moment(this.xMinValue)
          .add(5, "years")
          .startOf("years")
          .hours(1)
          .minutes(0)
          .seconds(0)
          .toDate();
        tempXMaxValue = moment(tempXMinValue).add(5, "years").toDate();
        break;
      default:
        const dayDiff = moment(this.xMaxValue)
          .add(1, "seconds")
          .diff(moment(this.xMinValue), "hours");
        tempXMinValue = this.xMaxValue;
        tempXMaxValue = moment(this.xMaxValue).add(dayDiff, "hours").toDate();
        break;
    }

    this.xMaxValue = tempXMaxValue;
    this.xMinValue = tempXMinValue;

    this._endDate = this.xMaxValue;
    this._startDate = this.xMinValue;

    await this._loaderService.dismissLoaderWaiting();
    await this.reloadDataAndSetOption();
  }

  /**
   * Handle click change granularity
   *
   * @param newGranularity New chart granularity
   */
  public async onClickChangeGranularity(
    newGranularity: ChartTimeUM
  ): Promise<void> {
    await this._loaderService.presentLoaderWaiting();
    this._yMaxValueChart = -1;
    this._yMinValueChart = -1;
    this.granularity = newGranularity;
    this.loadChartTitle();
    this.loadChartPeriod();
    let lenghtMaxInChart = 0;

    // if there is a new granularity load the addresses related to this Chart with the correct granularity
    // (if they don't exist the output address is the same of the input address)
    // When the addresses are received we substitute the previous address of the charts with the new and reload the data
    this._chartService
      .reloadChartAddressesOnDifferentGranularity(
        this.ComponentAPI.properties["Chart"],
        parseInt(newGranularity.toString(), 10)
      )
      .then(async (newAddresses: string[]) => {
        let lenghtData;
        if (newAddresses != null) {
          for (let i = 0; i < newAddresses.length; i++) {
            const element = newAddresses[i];
            const instanceguid_address = element.split("_", 2);
            this.ComponentAPI.chartSeries[i].instanceGUID =
              instanceguid_address[0];
            this.ComponentAPI.chartSeries[i].address = instanceguid_address[1];

            this._serieLoaded.get(this.ComponentAPI.chartSeries[i].id).data =
              [];

            lenghtData = await this.loadSerieData(
              this.ComponentAPI.chartSeries[i],
              this.xMinValue,
              this.xMaxValue
            );
            if (lenghtData > lenghtMaxInChart) {
              lenghtMaxInChart = lenghtData;
            }
          }
        }

        let totalElement = lenghtMaxInChart * newAddresses.length;
        this.loadOptionChart(
          this._yMaxValueChart,
          this._yMinValueChart,
          totalElement
        );
        await this._loaderService.dismissLoaderWaiting();
      });
  }

  /**
   * on the bases of the chart parametrization show a specific date interval on the xAxis
   */
  public loadChartPeriod(): void {
    switch (parseInt(this.ComponentAPI.properties["XBottomIntervalUM"], 10)) {
      case ChartTimeUM.PERIOD:
        this.xMinValue = moment(
          this.ComponentAPI.properties["XBottomStartDate"],
          "DD-MM-YYYY"
        ).toDate();
        break;
      case ChartTimeUM.DAY:
        this.xMinValue = moment()
          .subtract(
            parseInt(this.ComponentAPI.properties["XBottomInterval"], 10),
            "day"
          )
          .toDate();
        break;
      case ChartTimeUM.MONTH:
        this.xMinValue = moment()
          .subtract(
            parseInt(this.ComponentAPI.properties["XBottomInterval"], 10),
            "month"
          )
          .toDate();
        break;
      case ChartTimeUM.YEAR:
        this.xMinValue = moment()
          .subtract(
            parseInt(this.ComponentAPI.properties["XBottomInterval"], 10),
            "year"
          )
          .toDate();
        break;
      case ChartTimeUM.WEEK:
        this.xMinValue = moment()
          .subtract(
            parseInt(this.ComponentAPI.properties["XBottomInterval"], 10),
            "week"
          )
          .toDate();
        break;
      case ChartTimeUM.HOUR:
        this.xMinValue = moment()
          .subtract(
            parseInt(this.ComponentAPI.properties["XBottomInterval"], 10),
            "hour"
          )
          .toDate();
        break;
      case ChartTimeUM.MINUTE:
        this.xMinValue = moment()
          .subtract(
            parseInt(this.ComponentAPI.properties["XBottomInterval"], 10),
            "minute"
          )
          .toDate();
        break;
      case ChartTimeUM.SECOND:
        this.xMinValue = moment()
          .subtract(
            parseInt(this.ComponentAPI.properties["XBottomInterval"], 10),
            "second"
          )
          .toDate();
        break;
      default:
        this.xMinValue = moment(Date.now()).toDate();
    }

    switch (parseInt(this.ComponentAPI.properties["XBottomIntervalUM"], 10)) {
      case ChartTimeUM.PERIOD:
        this.xMaxValue = moment(
          this.ComponentAPI.properties["XBottomEndDate"],
          "DD-MM-YYYY"
        ).toDate();
        break;
      default:
        this.xMaxValue = moment().toDate();
        break;
    }

    this._endDate = this.xMaxValue;
    this._startDate = this.xMinValue;
  }

  /**
   * Reload data from date to date and reload option
   */
  private async reloadDataAndSetOption() {
    try {
      this.loadChartTitle();
      this._serieLoaded.forEach((value: any, key: number) => {
        value.data = [];
      });
      this._yMaxValueChart = -1;
      this._yMinValueChart = -1;
      let lenghtMaxInChart: number = 0;
      this.seriePIE = [];

      /*this.ComponentAPI.chartSeries.forEach(async (chartSerieAPI) => {
      let lenghtData = await this.loadSerieData(
        chartSerieAPI,
        this.xMinValue,
        this.xMaxValue
      );
      if (lenghtData > lenghtMaxInChart) {
        lenghtMaxInChart = lenghtData;
      }
      if (chartSerieAPI.kind != ChartSerieKind.PIE) {
        this.loadOptionChart(
          this._yMaxValueChart,
          this._yMinValueChart,
          lenghtMaxInChart
        );
      } else {
        if (this.seriePIE.length == 5) {
          this.loadPieChartOptions();
        }
      }
    });*/

      for (let chartSerieAPI of this.ComponentAPI.chartSeries) {
        let lenghtData = await this.loadSerieData(
          chartSerieAPI,
          this.xMinValue,
          this.xMaxValue
        );

        if (lenghtData > lenghtMaxInChart) {
          lenghtMaxInChart = lenghtData;
        }

        if (chartSerieAPI.kind != ChartSerieKind.PIE) {
          this.loadOptionChart(
            this._yMaxValueChart,
            this._yMinValueChart,
            lenghtMaxInChart
          );
        } else {
          if (this.seriePIE.length == this.ComponentAPI.chartSeries.length) {
            this.loadPieChartOptions();
          }
        }
      }
    } catch { }
  }

  /**
   * Load pie chart series options
   */
  private loadPieChartOptions(): void {
    this.arrayOfSeries[0]["data"] = this.seriePIE;

    this.options = {
      grid: [
        {
          backgroundcolor: this.ComponentAPI.properties["BackColor"],
          top: "5%",
          bottom: "30%",
          left: "20%",
        },
      ],
      legend: {
        show: true,
        type: "scroll",
        bottom: "10%",
        textStyle: {
          color: this.ComponentAPI.properties["LegendFontColor"],
          fontFamily: this.ComponentAPI.properties["LegendFontFamily"],
          fontSize: this.ComponentAPI.properties["LegendFontSize"],
          fontStyle:
            this.ComponentAPI.properties["LegendItalic"].toUpperCase() ===
              "TRUE"
              ? "italic"
              : "normal",
          fontWeight:
            this.ComponentAPI.properties["LegendBold"].toUpperCase() === "TRUE"
              ? "bold"
              : "normal",
        },
      },
      tooltip: {
        trigger: "item",
        formatter: function (params) {
          let um = params.data.um;
          if (um == null) {
            um = "";
          }
          return (
            params.name +
            ": " +
            params.data.value.toString().replace(".", ",") +
            " " +
            um +
            " " +
            "(" +
            params.percent.toString().replace(".", ",") +
            "%)"
          );
        },
        confine: true,
      },
      series: this.arrayOfSeries[0],
      color: this.legendColors,
    };

    this._chartInstance.setOption(this.options, true);
  }

  /**
   * Get  pie chart symblo
   *
   * @param pointStyle Point style
   */
  private getPieChartSymbol(pointStyle: string): string {
    let symbol: string = "";

    switch (parseInt(pointStyle)) {
      case ChartSeriePointStyle.ARROW_RIGHT:
        symbol = "arrow";
        break;
      case ChartSeriePointStyle.CIRCLE:
        symbol = "circle";
        break;
      case ChartSeriePointStyle.DIAMOND:
        symbol = "diamond";
        break;
      case ChartSeriePointStyle.SQUARE:
        symbol = "rect";
        break;
      case ChartSeriePointStyle.TRIANGLE_DOWN:
      case ChartSeriePointStyle.TRIANGLE_LEFT:
      case ChartSeriePointStyle.TRIANGLE_RIGHT:
      case ChartSeriePointStyle.TRIANGLE_UP:
        symbol = "triangle";
        break;
      case ChartSeriePointStyle.STAR:
        symbol = "pin";
        break;
      default:
        symbol = "circle";
        break;
    }

    return symbol;
  }

  /**
   * Get X Axxis formatting text by granularity and chart type
   *
   * @param dateTimeToFormat Value to format
   * @param index
   * @returns
   */
  private getXAsisTextFormatting(dateTimeToFormat: any): string {
    let textFormatted: string = dateTimeToFormat;

    if (
      this.chartKind == ChartSerieKind.LINE ||
      this.chartKind == ChartSerieKind.AREA
    ) {
      //textFormatted = this.formatXAxisTexForLineChart(dateTimeToFormat);
      textFormatted = this.formatXAxisTexForLineChart(dateTimeToFormat);
    }

    return textFormatted;
  }

  /**
   * Format date time x AXIS for bar chart
   *
   * @param dateTimeToFormat  Initial data
   * @returns Formatted date
   */
  private formatXAxisTexForBarChart(
    dateTimeToFormat: any,
    dateExtended: boolean
  ): string {
    let textFormatted: string = "";

    switch (this.granularity) {
      case ChartTimeUM.HOUR:
        textFormatted = moment(
          dateTimeToFormat,
          MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_STRAIGHT
        ).format(
          MomentFormats.MOMENT_DATETIME_DATE_EXTENDED +
          " " +
          MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTES
        );
        if (!dateExtended) {
          textFormatted = moment(
            dateTimeToFormat,
            MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_STRAIGHT
          ).format(
            MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_YEAR +
            " " +
            MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTES
          );
        }
        break;
      case ChartTimeUM.PERIOD:
      case ChartTimeUM.DAY:
        textFormatted = moment(
          dateTimeToFormat,
          MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_STRAIGHT
        ).format(MomentFormats.MOMENT_DATETIME_DATE_EXTENDED);
        if (!dateExtended) {
          textFormatted = moment(
            dateTimeToFormat,
            MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_STRAIGHT
          ).format(MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_YEAR);
        }

        break;
      case ChartTimeUM.WEEK:
        textFormatted =
          this.lblFrom +
          moment(
            dateTimeToFormat,
            MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_STRAIGHT
          ).format(MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_SLASH) +
          " " +
          this.lblTo +
          " " +
          moment(
            dateTimeToFormat,
            MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_STRAIGHT
          )
            .add(6, "d")
            .format(MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_SLASH);
        break;
      case ChartTimeUM.MONTH:
        textFormatted = moment(
          dateTimeToFormat,
          MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_MONTH_YEAR
        ).format("MMMM YYYY");
        break;

      case ChartTimeUM.YEAR:
        textFormatted = moment(dateTimeToFormat).format(
          MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_YEAR
        );
        break;
      default:
        textFormatted = dateTimeToFormat;
        break;
    }

    return textFormatted;
  }

  /**
   * Get number week of month based on date
   *
   * @param date Date to get week
   * @returns
   */
  private getWeekOfMountCount(date): number {
    let startDate = moment(date).startOf("month").date();
    let endDate = moment(date).date();
    let weekCount = 1;

    for (let i = startDate; i <= endDate; i++) {
      if (
        moment(date).date(i).format("dddd") ===
        moment().startOf("week").format("dddd")
      ) {
        weekCount++;
      }
    }

    return weekCount;
  }

  /**
   * Format X axis text for line chart
   *
   * @param dateTimeToFormat Date to format
   * @returns Formatted date
   */
  private formatXAxisTexForLineChart(dateTimeToFormat: string): string {
    let textFormatted: string = "";
    switch (this.granularity) {
      case ChartTimeUM.HOUR:
        textFormatted = moment(dateTimeToFormat).format(
          MomentFormats.MOMENT_DATETIME_DATE_EXTENDED +
          " " +
          MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTES
        );
        break;
      case ChartTimeUM.DAY:
        textFormatted = moment(dateTimeToFormat).format(
          MomentFormats.MOMENT_DATETIME_DATE_EXTENDED
        );
        break;
      case ChartTimeUM.MONTH:
        textFormatted = moment(dateTimeToFormat).format("MMMM YYYY");
        break;
      case ChartTimeUM.PERIOD:
      case ChartTimeUM.YEAR:
        textFormatted = moment(dateTimeToFormat).format("YYYY");
        break;
      case ChartTimeUM.WEEK:
        textFormatted =
          this.lblFrom +
          " " +
          moment(dateTimeToFormat).format("DD/MM") +
          " " +
          this.lblTo +
          " " +
          moment(dateTimeToFormat).add(6, "d").format("DD/MM");
        break;
      default:
        textFormatted = dateTimeToFormat;
        break;
    }

    return textFormatted;
  }

  /**
   * Format of the string dd-mm-yyy hh:mm:ss
   *
   * It was StaticClass.getMomentFromDateTimeString2 in the shared-components submodule, which dropped moment.
   *
   * @param value format of the string dd-mm-yyy hh:mm:ss
   */
  private static getMomentFromDateTimeString(value: string): moment.Moment {
    const timeStampSplit = value.split(" ");

    if (timeStampSplit.length > 1) {
      const originalDate = moment(timeStampSplit[0], "DD-MM-YYYY");

      originalDate.set("hour", parseInt(timeStampSplit[1].substr(0, 2), 10));
      originalDate.set("minute", parseInt(timeStampSplit[1].substr(3, 2), 10));
      originalDate.set("seconds", parseInt(timeStampSplit[1].substr(5, 2), 10));
      return moment(originalDate);
    } else {
      const originalDate = moment(timeStampSplit[0], "DD-MM-YYYY");

      return originalDate;
    }
  }
}
